import { WorkflowCode } from 'admin-panel/constants';

import type { TranslatedField } from 'admin-panel/constants/types';
import type ItemModel from 'admin-panel/models/item';
import type { CsvRow } from 'admin-panel/utils/csv';

const TRANSLATED_COLUMNS = [
  ['title', 'titleTranslations'],
  ['teaser', 'teaserTranslations'],
  ['content', 'contentTranslations'],
  ['link_url', 'linkUrlTranslations'],
  ['link_text', 'linkTextTranslations'],
] as const;

type TranslatedAttribute = typeof TRANSLATED_COLUMNS[number][1];

export type ItemCsvRecord = {
  active: boolean;
  visible: boolean;
  workflow: string;
  price: string;
} & Record<TranslatedAttribute, TranslatedField>;

function booleanFrom(value: string | undefined) {
  return !['false', '0', 'no'].includes((value ?? '').trim().toLowerCase());
}

function translationsFrom(row: CsvRow, column: string): TranslatedField {
  const prefix = `${column}_`;

  return Object.fromEntries(
    Object.entries(row)
      .filter(
        ([key, value]) => key.startsWith(prefix) && Boolean(value?.trim())
      )
      .map(([key, value]) => [key.slice(prefix.length), value])
  );
}

export function itemCsvRecord(row: CsvRow): ItemCsvRecord {
  return {
    active: booleanFrom(row['active']),
    visible: booleanFrom(row['visible']),
    workflow: row['workflow']?.trim() || WorkflowCode.InformationItem,
    price: row['price']?.trim() ?? '',
    ...(Object.fromEntries(
      TRANSLATED_COLUMNS.map(([column, attribute]) => [
        attribute,
        translationsFrom(row, column),
      ])
    ) as Record<TranslatedAttribute, TranslatedField>),
  };
}

export function itemCsvRecordTitle(record: ItemCsvRecord) {
  return Object.values(record.titleTranslations)[0]?.trim() ?? '';
}

export function itemCsvRow(
  item: ItemModel,
  price: string,
  localeKeys: string[]
): CsvRow {
  const row: CsvRow = {
    active: String(item.active !== false),
    visible: String(item.visible !== false),
    workflow: item.workflow ?? '',
    price,
  };

  TRANSLATED_COLUMNS.forEach(([column, attribute]) => {
    localeKeys.forEach((localeKey) => {
      row[`${column}_${localeKey}`] = item[attribute]?.[localeKey] ?? '';
    });
  });

  return row;
}
